import { Dispatch, SetStateAction } from 'react';
import { ClientSubscription, Country as CountryObject, OpaSubscription } from '@models/Opa';
import { RetailerClient } from '@models/RetailerClient';
import { ProductDataType, ProductType } from '@pages/product-analyzer/Product';
import { BaseTimePeriod, MainFilters } from '@services/FiltersTypes';
import { constructRetailerClientState } from '@state/reducers/retailer-client/RetailerClientUtils';
import { getAndStoreSavedFilters, getSubscriptionDetailsByProductType } from '@utils/AppSubscriptionUtils';
import { constructCountryOptionForSideMenu, getFiltersForClient, getLatestPeriod } from '@utils/FilterUtils';
import saveErrorLog, { ErrorTagEnum } from '@utils/LogUtil';
import { NO_OPTIONS } from '@utils/StringConstants';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  Country,
  FeatureOption,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
  SelectedFilters,
} from '@utils/pageConfig/Types';
import { GrowthTypes } from '../comparison-growth-selector/Types';
import { RelativePeriod } from '../comparison-period-selector/Types';
import { Periodicity } from '../time-period-selector/PeriodsTypes';
import { RetailerClientState } from '@state/reducers/retailer-client/RetailerClientReducer';
import { MarketPremiumRetailerClientState } from '@state/reducers/market-premium-retailer-client/MarketPremiumRetailerClientReducer';
import { batch } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  actionCreators,
  actionCreatorsHitlistsRetailerClient,
  actionCreatorsInitialFilters,
  actionCreatorsInitialFiltersMarketPremium,
  actionCreatorsInitialFiltersProducts,
  actionCreatorsMarketPremiumClientState,
  actionCreatorsProducts,
  actionCreatorsRetailerClient,
  actionCreatorsSideFilters,
} from '@state/Index';
// Convert the Country object based on the countries from subscriptions
export const mapSubscribedCountries = (subscriptions: ClientSubscription[]): Country[] => {
  return subscriptions.map((sub) => {
    return {
      id: sub.countryObject.countryCode,
      name: sub.countryObject.countryName,
      currency: sub.countryObject.currency.currencyCode,
      currencyLabel: sub.countryObject.currency.currencyLabel,
    };
  });
};

// If selected country is exists in the subscription, retain the selection, else select the first country option from the subscription
export const mapDefaultSubscribedCountry = (
  subscriptions: ClientSubscription[],
  selectedCountries: Option[], // We only support single country, so it should be always single option
): CountryObject => {
  const defaultSubscribedCountryObj = subscriptions[0].countryObject;
  return subscriptions.some((subscription) => subscription.countryObject.countryCode === selectedCountries[0].value)
    ? subscriptions.find((subscription) => subscription.countryObject.countryCode === selectedCountries[0].value)!
        .countryObject
    : defaultSubscribedCountryObj;
};

export const mapCountryObjForFilters = (countryObj: CountryObject): Filters => {
  return {
    dataType: 'COUNTRY',
    feature: 'COUNTRY',
    filterTitle: 'Country',
    filterKey: FilterKeys.COUNTRY,
    options: [
      {
        label: countryObj.countryName,
        value: countryObj.countryCode,
      },
    ],
    disabled: false,
  };
};

export const retainTimeFrame = (mainFilter: MainFilters, currentTimeFrame: Periodicity): Periodicity => {
  const {
    timePeriod: { weekly, monthly },
  } = mainFilter;

  if (!weekly.length) {
    return Periodicity.Monthly;
  }

  if (!monthly.length) {
    return Periodicity.Weekly;
  }

  return currentTimeFrame;
};

// It should retain the previous selected timePeriod value if exists, otherwise, select the timePeriod which is available in the filters
export const retainTimePeriod = (filtersDataBasedOnCountry: MainFilters, timeFrame: Periodicity): BaseTimePeriod => {
  const weeklyTimePeriod = filtersDataBasedOnCountry.timePeriod.weekly;
  const monthlyTimePeriod = filtersDataBasedOnCountry.timePeriod.monthly;

  if (timeFrame === Periodicity.Weekly) {
    return constructTimePeriodSelection(weeklyTimePeriod[getLatestPeriod(weeklyTimePeriod)]);
  }

  return constructTimePeriodSelection(monthlyTimePeriod[getLatestPeriod(monthlyTimePeriod)]);
};

export const constructTimePeriodSelection = (option: BaseTimePeriod): BaseTimePeriod => {
  if (!option) {
    return {
      label: '',
      value: '',
      lastUpdatedDate: 0,
    };
  }
  return {
    label: option.label,
    value: option.value,
    lastUpdatedDate: option.lastUpdatedDate,
  };
};

export const constructCountryOption = (countryObj: CountryObject): Option[] => {
  return [
    {
      label: countryObj.countryName,
      value: countryObj.countryCode,
    },
  ];
};

export const getUniqueSubscriptions = (clientSubscriptions: OpaSubscription, clientId: number) => {
  const uniqueSubscriptions: ClientSubscription[] = [];
  clientSubscriptions.result.restrictions
    .filter((subscription) => +subscription.retailer === +clientId)
    .forEach((client) => {
      if (uniqueSubscriptions.map((subscription) => subscription.country).indexOf(client.country) === -1) {
        uniqueSubscriptions.push(client);
      }
    });
  return uniqueSubscriptions;
};

export const saveErrorLogForOnChange = (countries: Country[], appliedFilters: SelectedFilters[], pageName: string) => {
  const errorMessageFilterBasedOnCountry = 'Filters cannot be found based on selected country';
  saveErrorLog(new Error(errorMessageFilterBasedOnCountry), `${pageName} - ${errorMessageFilterBasedOnCountry}.`, {
    category: ErrorTagEnum.DataError,
    requestPayload: {
      countries: countries,
      appliedFilters: appliedFilters,
      pageType: PageType,
    },
  });
};

export const getSubscriptionForOtherProducts = (
  productType: ProductType,
  pagesConfig: PagesConfig,
  newRetailerId: number,
) => {
  const subscriptionDetails = getSubscriptionDetailsByProductType(productType, pagesConfig);
  return getUniqueSubscriptions(subscriptionDetails.currentSubscription, newRetailerId);
};

export const onChangeHandlerForOtherProducts = async (methodParams: OnRetailerChangeParam) => {
  const defaultCountry = mapDefaultSubscribedCountry(methodParams.subscriptions, methodParams.appliedCountries);
  const defaultCountryCode = defaultCountry.countryCode;

  await getAndStoreSavedFilters(
    methodParams.productType,
    methodParams.defaultClientId,
    methodParams.pagesConfig,
    methodParams.pagesConfig.userId,
  );

  // Call filters API for Product Analyzer
  const filtersData = await getFiltersForClient(
    methodParams.defaultClientId,
    methodParams.subscribedCountries,
    methodParams.setLoadingOverlay,
    methodParams.productType,
    methodParams.productDataType,
  );

  const filtersDataBasedOnCountry = filtersData.find((filter) => filter.country === defaultCountryCode);

  if (!filtersDataBasedOnCountry) {
    const countryMapping: Record<ProductType, Country[]> = {
      [ProductType.PerformanceAnalyzer]: methodParams.pagesConfig.clientCountries,
      [ProductType.MarketPremium]: methodParams.pagesConfig.marketPremiumClientCountries,
      [ProductType.ProductAnalyzer]: methodParams.pagesConfig.hitlistsClientCountries,
      [ProductType.SellerInsights]: methodParams.pagesConfig.sellerInsightsClientCountries,
      [ProductType.RetailKad]: methodParams.pagesConfig.clientCountries,
    };

    const countries = countryMapping[methodParams.productType];
    saveErrorLogForOnChange(countries, methodParams.pagesConfig.appliedFilters, methodParams.pageName);
    return;
  }

  constructCountryOptionForSideMenu(methodParams.pagesConfigAllFilters, defaultCountry, methodParams.pageList);

  // Get time frame and period for Product Analyzer
  const currTimeFrame = retainTimeFrame(filtersDataBasedOnCountry, methodParams.timeFrame);
  const currPeriod = retainTimePeriod(filtersDataBasedOnCountry, currTimeFrame);

  return {
    currTimeFrame,
    currPeriod,
    defaultCountryCode,
    filtersData,
  };
};

export interface OnRetailerChangeParam {
  subscriptions: ClientSubscription[];
  appliedCountries: Option[];
  defaultClientId: number;
  pagesConfig: PagesConfig;
  productType: ProductType;
  productDataType: ProductDataType;
  subscribedCountries: Country[];
  setLoadingOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  timeFrame: Periodicity;
  pagesConfigAllFilters: SelectedFilters[];
  pageName: string;
  pageList: PageType[];
}

export const getSelectedClientNameWithClientId = (
  retailerClients: RetailerClient[],
  clientId: string | number | undefined,
) => {
  if (!retailerClients || !clientId) {
    return NO_OPTIONS;
  }

  const selectedRetailer = retailerClients.find((option) => option.clientId === +clientId);

  if (!selectedRetailer) {
    return NO_OPTIONS;
  }

  return selectedRetailer.clientName;
};

export type ResetSequenceFuncParam = {
  defaultClientId: number;
  subscribedCountries: Country[];
  hitlistsSubscriptions: ClientSubscription[];
  hitlistsSubscribedCountries: Country[];
  defaultCountry: CountryObject;
  pagesConfigAllFilters: SelectedFilters[];
  filtersDataBasedOnCountry: MainFilters;
  currTimeFrame: Periodicity;
  selectedProductGroup?: Option[];
  productType?: ProductType;
};

export type RetailerChangeHandlerParam = {
  pagesConfig: PagesConfig;
  appliedCountries: Option[];
  retailerClients: RetailerClient[];
  setLoadingOverlay: Dispatch<SetStateAction<boolean>>;
  timeFrame: Periodicity;
  pageName: PageName;
  selectedGrowthType: GrowthTypes;
  selectedGrowthTypeForProducts: GrowthTypes;
  setDefaultFilters: Dispatch<SetStateAction<MainFilters[]>>;
  feature: FeatureOption;
  selectedProductGroup?: Option[];
  productType?: ProductType;
  updateRetailerClientAction?:
    | ((payload: RetailerClientState) => (dispatch: any) => void)
    | ((payload: MarketPremiumRetailerClientState) => (dispatch: any) => void);
  resetSequenceFunc: (param: ResetSequenceFuncParam) => Promise<void>;
};

export const onRetailerChange = async (
  subscriptions: ClientSubscription[],
  props: RetailerChangeHandlerParam,
  dispatch: any,
) => {
  // all action creators
  const { updateRetailerClient } = bindActionCreators(actionCreatorsRetailerClient, dispatch);
  const { updateMarketPremiumRetailerClient } = bindActionCreators(actionCreatorsMarketPremiumClientState, dispatch);
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);
  const { updateInitialFilters } = bindActionCreators(actionCreatorsInitialFilters, dispatch);
  const { updateInitialFiltersMarketPremium } = bindActionCreators(actionCreatorsInitialFiltersMarketPremium, dispatch);
  const { updateMainFilters } = bindActionCreators(actionCreators, dispatch);
  const { updateInitialFiltersProducts } = bindActionCreators(actionCreatorsInitialFiltersProducts, dispatch);
  const { updateMainFiltersProducts } = bindActionCreators(actionCreatorsProducts, dispatch);
  const { updateHitlistsRetailerClient } = bindActionCreators(actionCreatorsHitlistsRetailerClient, dispatch);

  const {
    pagesConfig,
    appliedCountries,
    retailerClients,
    setLoadingOverlay,
    timeFrame,
    pageName,
    selectedGrowthType,
    selectedGrowthTypeForProducts,
    setDefaultFilters,
    feature,
    selectedProductGroup,
    productType = ProductType.PerformanceAnalyzer,
    updateRetailerClientAction = updateRetailerClient,
    resetSequenceFunc,
  } = props;

  setLoadingOverlay(true);

  const pageTypeTAorOR =
    pageName === PageName.TRADING_ASSISTANT ? PageName.TRADING_ASSISTANT : PageName.OPPORTUNITY_REVIEW;

  // Default clientId, but the clientId should be always the same no matter how many countries the client has
  const defaultClientId = subscriptions[0].retailer;

  const subscribedCountries = mapSubscribedCountries(subscriptions);

  // Display saved filter according to selected retailer for both product types
  await getAndStoreSavedFilters(productType, defaultClientId, pagesConfig, pagesConfig.userId);

  // Get the current side menu filters selection from pagesConfig
  const pagesConfigAllFilters = [...pagesConfig.appliedFilters];

  // get hitlists subscription. current assumption is that it exist
  const hitlistsSubscriptions = getSubscriptionForOtherProducts(
    ProductType.ProductAnalyzer,
    pagesConfig,
    defaultClientId,
  );
  const hitlistsSubscribedCountries = mapSubscribedCountries(hitlistsSubscriptions);

  // Default country, retain the countries selection if exists in the subscription
  const defaultCountry = mapDefaultSubscribedCountry(subscriptions, appliedCountries);
  const defaultCountryCode = defaultCountry.countryCode;

  //Retrieve client name
  const selectedClientName = getSelectedClientNameWithClientId(retailerClients, defaultClientId);

  if (hitlistsSubscribedCountries.length !== 0) {
    const onChangeMethodParam: OnRetailerChangeParam = {
      subscriptions: hitlistsSubscriptions,
      appliedCountries,
      defaultClientId,
      pagesConfig,
      productType: ProductType.ProductAnalyzer,
      productDataType: ProductDataType.ProductAnalyzer,
      subscribedCountries: hitlistsSubscribedCountries,
      setLoadingOverlay: (_) => {},
      timeFrame,
      pagesConfigAllFilters,
      pageName,
      pageList: [PageType.RI_PRODUCT],
    };
    await onChangeHandlerForOtherProducts(onChangeMethodParam).then((response) => {
      if (!response) {
        return;
      }
      // if update to react 18 and above, can remove call to batch function
      batch(() => {
        updateMainFiltersProducts({
          timeFrame: response.currTimeFrame,
          period: response.currPeriod,
          comparisonPeriod: RelativePeriod.PreviousYearCurrentPeriod,
          selectedGrowthType: selectedGrowthTypeForProducts,
          country: response.defaultCountryCode,
        });

        updateHitlistsRetailerClient(
          constructRetailerClientState(
            defaultClientId,
            hitlistsSubscribedCountries,
            selectedClientName,
            pageTypeTAorOR,
          ),
        );
        updateInitialFiltersProducts({ initialFilters: response.filtersData });
      });
    });

    constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [PageType.RI_PRODUCT]);
  }

  const filtersData = await getFiltersForClient(
    defaultClientId,
    subscribedCountries,
    (_) => {},
    productType,
    ProductDataType.PerformanceAnalyzer,
  );

  setDefaultFilters(filtersData);

  const filtersDataBasedOnCountry = filtersData.find((filter) => filter.country === defaultCountryCode);

  if (!filtersDataBasedOnCountry) {
    saveErrorLogForOnChange(pagesConfig.clientCountries, pagesConfig.appliedFilters, pageTypeTAorOR);
    setLoadingOverlay(false);
    return;
  }

  constructCountryOptionForSideMenu(pagesConfigAllFilters, defaultCountry, [
    PageType.RETAILER_OVERVIEW,
    PageType.CATEGORY_LEADERBOARD,
    PageType.OPPORTUNITY_REVIEW,
    PageType.PERFORMANCE_ANALYSIS,
    PageType.TRADING_ASSISTANT,
  ]);

  const currTimeFrame = retainTimeFrame(filtersDataBasedOnCountry, timeFrame);
  const mainFilterState = {
    selectedGrowthType: selectedGrowthType,
    feature: feature,
  };

  const storeFilterSettings = {
    appliedFilters: pagesConfigAllFilters,
  };
  const marketPremiumSubscription = getSubscriptionForOtherProducts(
    ProductType.MarketPremium,
    pagesConfig,
    defaultClientId,
  );
  const marketPremiumSubscribedCountries = mapSubscribedCountries(marketPremiumSubscription);

  // if update to react 18 and above, can remove call to batch function
  batch(async () => {
    updateSideFilters(storeFilterSettings);
    updateMainFilters(mainFilterState);
    updateRetailerClientAction(
      constructRetailerClientState(defaultClientId, subscribedCountries, selectedClientName, pageTypeTAorOR),
    );
    /**
     * this is to update the RI page's retailer client id for retaining across all ri pages and TA page
     **/
    updateRetailerClient(
      constructRetailerClientState(defaultClientId, subscribedCountries, selectedClientName, pageTypeTAorOR),
    );
    /**
     * this is to update the TA page's retailer client id for retaining across all ri pages and TA page
     **/
    if (marketPremiumSubscribedCountries.length > 0) {
      updateMarketPremiumRetailerClient(
        constructRetailerClientState(defaultClientId, subscribedCountries, selectedClientName, pageTypeTAorOR),
      );
      updateInitialFiltersMarketPremium({ initialFilters: filtersData });
    }
    updateInitialFilters({ initialFilters: filtersData });

    await resetSequenceFunc({
      defaultClientId,
      subscribedCountries,
      hitlistsSubscriptions,
      hitlistsSubscribedCountries,
      defaultCountry,
      pagesConfigAllFilters,
      filtersDataBasedOnCountry,
      currTimeFrame,
      selectedProductGroup,
    });
  });

  setLoadingOverlay(false);
};

export const getRetailerDefaultCountryCode = (
  retailerClientState: RetailerClientState,
  appliedCountries: Option[],
): string => {
  return retailerClientState.countries.some((country: Country) => country.id === appliedCountries[0].value)
    ? retailerClientState.countries.find((country: Country) => country.id === appliedCountries[0].value)?.id!
    : retailerClientState.countries[0].id;
};
